import { AOS_DS_MERGE_CONFIG, OUTLINE_STYLE_CLASSES } from 'aos-ds/src/tailwind/merge'

/**
 * The named font sizes the Tailwind preset defines. tailwind-merge cannot tell
 * `text-body-md` (a size) from `text-status-critical` (a colour) on its own, because
 * both are `text-*` with an unknown suffix, so it treats the size as a colour and
 * drops one of them. Listing them here is what separates the two groups.
 *
 * Keep in step with `fontSize` in `tailwind-preset.js`; `mergeConfig.spec.ts` fails
 * if they drift.
 */
export const NAMED_FONT_SIZES = [
    'label-sm',
    'label-md',
    'label-lg',
    'body-sm',
    'body-md',
    'heading-sm',
    'heading-md',
    'heading-lg',
] as const

export const FONT_SIZE_CLASSES = NAMED_FONT_SIZES.map(size => `text-${size}`)

/**
 * Re-exported so this package's own call sites keep one name for it. The group is
 * defined in `aos-ds`, because that is where the Button merges the class.
 */
export { OUTLINE_STYLE_CLASSES }

/**
 * Goes inside `tailwind-variants`' `twMergeConfig`. It resolves tailwind-merge
 * separately from the repo — the repo is on v3, `tailwind-variants` declares v1 — and
 * the two read a different shape: v1 takes `classGroups` at the top level, v3 takes
 * `extend.classGroups`. Both are given, because which one answers depends on how the
 * bundler resolved the nested copy, and an unrecognised key is ignored rather than
 * rejected. `merge.spec.ts` asserts the result, not the shape.
 */
/**
 * The aos-ds scale, folded in so a class from a contract behaves here too.
 *
 * `aos-ds` components resolve their classes from Figma tokens (`px-m`, `gap-xs`,
 * `rounded-common`), and this package merges those classes whenever it wraps or
 * composes one. Without the groups below, tailwind-merge reads them as unknown
 * classes and a call-site override silently stops winning — see
 * `aos-ds/src/tailwind/merge.ts`.
 */
const AOS_DS_CLASS_GROUPS = AOS_DS_MERGE_CONFIG.extend.classGroups

export const TV_MERGE_CONFIG = {
    classGroups: {
        'font-size': FONT_SIZE_CLASSES,
        ...AOS_DS_CLASS_GROUPS,
    },
    extend: {
        classGroups: {
            'font-size': FONT_SIZE_CLASSES,
            ...AOS_DS_CLASS_GROUPS,
        },
    },
}

/** tailwind-merge v3 shape, for the repo's own `twMerge` call sites. */
export const TW_MERGE_CONFIG = {
    extend: {
        classGroups: {
            'font-size': FONT_SIZE_CLASSES,
            ...AOS_DS_CLASS_GROUPS,
        },
    },
}
